import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { en } from './locales/en';
import { ms } from './locales/ms';

export const DEFAULT_LOCALE = 'en';

/** Locales bundled into the panel iframe (not the host loader). */
export const supportedLocales = ['en', 'ms'] as const;
export type LocaleCode = (typeof supportedLocales)[number];

/** Normalize `ms-MY` / `en_US` → catalog code, else fallback. */
export function resolveLocale(input?: string | null): LocaleCode {
  if (!input) return DEFAULT_LOCALE;
  const normalized = input.trim().replace(/_/g, '-').toLowerCase();
  const base = (normalized.split('-')[0] || normalized) as LocaleCode;
  if ((supportedLocales as readonly string[]).includes(base)) return base;
  if ((supportedLocales as readonly string[]).includes(normalized)) {
    return normalized as LocaleCode;
  }
  return DEFAULT_LOCALE;
}

/**
 * Panel-only i18n. The customer embed script (`loader.js`) stays free of
 * i18next so host-page weight stays tiny; this runs inside the lazy iframe.
 */
void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    ms: { translation: ms },
  },
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...supportedLocales],
  nonExplicitSupportedLngs: true,
  interpolation: {
    // React already escapes output
    escapeValue: false,
  },
  // All locales shipped inline — no HTTP backend / extra network
  react: {
    useSuspense: false,
  },
});

export function applyDocumentLocale(locale: LocaleCode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
  document.title = i18n.getFixedT(locale)('doc.title');
}

export async function changeLocale(input?: string | null): Promise<LocaleCode> {
  const locale = resolveLocale(input);
  if (i18n.language !== locale) {
    await i18n.changeLanguage(locale);
  }
  applyDocumentLocale(locale);
  return locale;
}

export default i18n;
